import { z } from 'zod';
import { isInternalAppPath, isLeafKind, type AdminMenuItem, type MenuKind } from '@/types/menu';

/**
 * Form + tree rules for the Menu config screen (migration 131; SPEC §9.1). The
 * server re-checks all of it (api/MenuValidation.cs); these exist so the admin
 * sees the reason before a round trip.
 */

export const menuItemSchema = z
  .object({
    Kind: z.enum(['Menu', 'Submenu', 'Route', 'Report']),
    Title: z.string().trim().min(1, 'Title is required').max(200, 'Title must be 200 characters or fewer'),
    Description: z.string().max(1000, 'Description must be 1000 characters or fewer'),
    Icon: z.string(),
    Url: z.string().max(1000, 'Must be 1000 characters or fewer'),
    Target: z.enum(['_self', '_blank']),
    IsActive: z.boolean(),
    Roles: z.array(z.string()),
  })
  .superRefine((d, ctx) => {
    const url = d.Url.trim();
    if (d.Kind === 'Route') {
      if (!url) ctx.addIssue({ code: 'custom', path: ['Url'], message: 'A page needs an app path, for example /assets' });
      else if (!isInternalAppPath(url))
        ctx.addIssue({ code: 'custom', path: ['Url'], message: 'Start with a single / (for example /assets). For an external address, choose Link.' });
    }
    if (d.Kind === 'Report' && !url) ctx.addIssue({ code: 'custom', path: ['Url'], message: 'A link needs a URL' });
  });
export type MenuItemFormData = z.infer<typeof menuItemSchema>;

/**
 * The kinds that may sit under a parent of `parentKind` (null = top level).
 * Mirrors MenuValidation.ValidatePlacement: categories are top level, a
 * sub-category lives in a category, and nothing nests deeper.
 */
export function kindsAllowedUnder(parentKind: MenuKind | null): MenuKind[] {
  if (parentKind === null) return ['Menu', 'Route', 'Report'];
  if (parentKind === 'Menu') return ['Submenu', 'Route', 'Report'];
  if (parentKind === 'Submenu') return ['Route', 'Report'];
  return [];
}

export interface TreeNode extends AdminMenuItem {
  children: TreeNode[];
}

export function buildTree(items: AdminMenuItem[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  items.forEach((i) => byId.set(i.MenuItemId, { ...i, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const parent = node.ParentId != null ? byId.get(node.ParentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.SortOrder - b.SortOrder || a.Title.localeCompare(b.Title));
    nodes.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

/**
 * Where `item` may be moved: the destinations its kind allows, minus where it
 * already is. `null` = top level. A category has none — it is top level only,
 * and changes place with Move up / Move down.
 */
export function moveTargets(item: AdminMenuItem, all: AdminMenuItem[]): Array<AdminMenuItem | null> {
  const allowedParentKinds: Array<MenuKind | null> = (['Menu', 'Submenu', null] as Array<MenuKind | null>)
    .filter((pk) => kindsAllowedUnder(pk).includes(item.Kind));
  const targets: Array<AdminMenuItem | null> = [];
  if (allowedParentKinds.includes(null) && item.ParentId !== null) targets.push(null);
  for (const c of all) {
    if (isLeafKind(c.Kind) || c.MenuItemId === item.MenuItemId || c.MenuItemId === item.ParentId) continue;
    if (!allowedParentKinds.includes(c.Kind)) continue;
    targets.push(c);
  }
  return targets;
}

/** Up / down within its siblings: the full sibling list renumbered 1..n. */
export function swapOrder(siblings: AdminMenuItem[], id: number, dir: -1 | 1) {
  const i = siblings.findIndex((s) => s.MenuItemId === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= siblings.length) return null;
  const next = [...siblings];
  [next[i], next[j]] = [next[j], next[i]];
  return next.map((s, k) => ({ MenuItemId: s.MenuItemId, ParentId: s.ParentId, SortOrder: k + 1 }));
}
